import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowUpIcon, UsersIcon } from "@heroicons/react/24/solid";
import { toast } from "@components/toast/Toast";
import { useCustomerDetails, useSaveSmartNote } from "@api/customersController";
import {
  useAcceptTask,
  useCancelAction,
  useDecideAction,
  warmContext,
} from "@api/intelligenceController";
import { Markdown } from "@areas/chatbot/utils";
import { useUser } from "@auth";
import { useHistoricalAnalysisMcp, useAiResponseTimer } from "@flags";
import BatchTemplateRequestModal from "@areas/trade-accounts/flows/BatchTemplateRequestModal";
import PaymentRequestDetails from "@areas/payments/displays/PaymentRequestDetails";
import PlanDetailModal from "@areas/calendar/displays/PlanDetailModal";
import MentionTextarea from "./MentionTextarea";
import AiSpinner from "./AiSpinner";
import AiSparkleIcon from "./AiSparkleIcon";
import CustomerContextPanel from "./CustomerContextPanel";
import DraftSuggestionCard, { isActionBacked, isSendEmailType } from "./DraftSuggestionCard";
import SuggestionComponentCard from "./SuggestionComponentCard";
import ChartCard from "./ChartCard";
import { getSuggestionComponent } from "./suggestionComponents";
import CancelActionConfirmModal from "./CancelActionConfirmModal";
import { cancelledActivityCopy } from "./cancellationCopy";
import SendEmailPreviewModal from "./SendEmailPreviewModal";
import CapabilitiesHelp, { useFirstRunTour } from "./CapabilitiesHelp";
import {
  buildAmendments,
  buildEmailAmendments,
  editablePaymentStep,
  isStatementStep,
  stepToRequestConfig,
} from "./actionAmendments";
import useSignalChat from "./useSignalChat";

const PROMPT_PLACEHOLDER =
  "You can add a note, send a request or type @ to notify a colleague";

const READ_LOADING_PLACEHOLDER = "How can I help you today?";

const THINKING_PLACEHOLDER = "Ontik is thinking…";

function timeOfDayGreeting(hour) {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

// The selected account, shown as a removable pill inside the input row.
function AccountChip({ customer, onClear }) {
  return (
    <div className="inline-flex w-fit items-center gap-2 rounded-full border border-zinc-300 py-1 pl-1 pr-3 text-sm">
      <span className="flex size-6 items-center justify-center rounded-full bg-zinc-800 text-white">
        <UsersIcon aria-hidden="true" className="size-3.5" />
      </span>
      <span className="font-medium text-zinc-900">{customer.name}</span>
      {customer.accountCode && (
        <span className="text-zinc-400">{customer.accountCode}</span>
      )}
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear account"
        className="ml-1 text-zinc-700 hover:text-zinc-900"
      >
        <XMarkIcon aria-hidden="true" strokeWidth={2.5} className="size-4" />
      </button>
    </div>
  );
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The requested amount off an action-backed payment request's composed payment.request.send
// step — best-effort, for the optimistic activity-feed row only (the canonical row reconciles
// from the realtime ActivityCreated event regardless).
function paymentRequestAmount(steps) {
  return (
    steps?.find((s) => s.stepType === "payment.request.send")?.payload
      ?.amount_requested ?? null
  );
}

// Highlights @-mentions in blue, matching full known names (mentions can be multi-word).
function PromptText({ text, mentionNames = [] }) {
  if (mentionNames.length === 0) return text;

  const tokens = new Set(mentionNames.map((name) => `@${name}`));
  const pattern = [...tokens]
    .sort((a, b) => b.length - a.length) // longest first so multi-word names win
    .map(escapeRegExp)
    .join("|");

  return text.split(new RegExp(`(${pattern})`, "g")).map((part, i) =>
    tokens.has(part) ? (
      <span key={i} className="font-medium text-blue-600">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// Assistant reply bubble. The reply arrives complete in a single frame (token streaming is off), so it
// renders in full at once with the same fade the cards use — no character-by-character crawl, which used
// to flood the turn with renders and make the card wait out a variable-length type-out. The cards render
// in the same paint beside it (no gate), so reply and card land together every time. onGrow pins the
// thread only on a pure-chat turn — when there's a card, the card owns the scroll.
function AssistantReply({ text, onGrow }) {
  useEffect(() => {
    onGrow?.();
  }, [text, onGrow]);

  return (
    <div className="animate-fadeIn w-full text-sm text-zinc-800">
      <Markdown>{text}</Markdown>
    </div>
  );
}

export function SmartNoteModal({ isOpen, onClose, initialCustomer = null }) {
  const [draft, setDraft] = useState({
    text: "",
    mentionedCustomerIds: [],
    mentionedUserIds: [],
    mentions: [],
    llmText: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // The customer the running thread belongs to (snapshotted on submit) — context for
  // accepting/rejecting a suggestion (customerId, note-card attribution).
  const [analysedCustomer, setAnalysedCustomer] = useState(null);

  // Entry points other than the @-search open Smart Note with only { id, name, accountCode }, but the
  // request modals derive bucket amounts and contacts from the full record — without it they show £0.
  const requestCustomerBase = analysedCustomer ?? selectedCustomer;
  const { data: requestCustomerDetail } = useCustomerDetails(requestCustomerBase?.id);
  const requestCustomer =
    requestCustomerBase && requestCustomerDetail
      ? { ...requestCustomerBase, ...requestCustomerDetail }
      : requestCustomerBase;

  // Pre-select a customer when the modal is opened for one (e.g. a task's "view context"); the
  // spacebar path passes none, so the user picks a customer themselves.
  useEffect(() => {
    if (isOpen && initialCustomer) setSelectedCustomer(initialCustomer);
  }, [isOpen, initialCustomer]);

  // Remount the editor to clear it after each submitted turn.
  const [editorKey, setEditorKey] = useState(0);

  // Per-suggestion state, keyed `${messageId}:${signalIndex}` (suggestions live
  // inside assistant turns across the whole thread). Accepted suggestions stay on
  // screen and flip to a tick rather than being removed; rejected ones flip to a
  // greyed-out cross.
  const [done, setDone] = useState(new Set());
  const [rejected, setRejected] = useState(new Set());
  const [pending, setPending] = useState(new Set());

  // The suggestion being edited in the batch request modal (null = closed): the user
  // tweaks the payment request step's details, and the modal's Accept applies them as
  // step amendments on the action accept call.
  const [editTarget, setEditTarget] = useState(null);
  const [editDocsTarget, setEditDocsTarget] = useState(null);
  // A payment send step being previewed read-only from a timeline (null = closed): the step plus,
  // for a plan, its instalment position so the modal can title itself "Instalment N of M".
  const [preview, setPreview] = useState(null);
  const [helpOpen, setHelpOpen] = useFirstRunTour(isOpen);
  // The cancellation suggestion awaiting confirmation in the calendar-style modal (null =
  // closed): the card's Confirm opens the gate, and only the modal's confirm actually cancels.
  const [cancelTarget, setCancelTarget] = useState(null);

  const { user } = useUser();
  const chartsEnabled = useHistoricalAnalysisMcp();
  const responseTimerEnabled = useAiResponseTimer();
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const greeting = timeOfDayGreeting(new Date().getHours());
  const title = firstName ? `${greeting}, ${firstName}` : greeting;

  const queryClient = useQueryClient();
  // Imperative handle on the context panel — the task accept asks it to reconcile the feed and
  // Ontik read if the realtime hint is missed (see CustomerContextPanel.reconcileActivity).
  const contextPanelRef = useRef(null);
  const saveMutation = useSaveSmartNote();
  const decideAction = useDecideAction();
  const acceptTask = useAcceptTask();
  const cancelAction = useCancelAction();
  const { messages, isThinking, thinkingLabel, send, reset, appendSuggestion, lastCardMs } =
    useSignalChat();

  // Everything scoped to the customer currently being analysed: the thread, its
  // suggestions, and any in-flight edit. Cleared on customer switch and on close.
  const clearConversation = useCallback(() => {
    reset();
    setAnalysedCustomer(null);
    setDone(new Set());
    setRejected(new Set());
    setPending(new Set());
    setEditTarget(null);
    setEditDocsTarget(null);
  }, [reset]);

  // Switching accounts abandons the previous customer's thread — its context and
  // suggestions belong to the old customer and must not carry over.
  useEffect(() => {
    clearConversation();
  }, [selectedCustomer?.id, clearConversation]);

  // The "Ontik read" holds the composer's cycling example placeholder back until
  // it has finished typing out — so only one typewriter runs at a time. The
  // SummaryCard reports readiness; reset it whenever the account changes.
  const [readReady, setReadReady] = useState(false);
  useEffect(() => {
    setReadReady(false);
  }, [selectedCustomer?.id]);

  // Keep the thread pinned to the latest turn as it grows (including while a reply types out).
  const scrollRef = useRef(null);
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking, scrollToBottom]);

  const canSubmit = !!selectedCustomer && draft.text.trim().length > 0;

  // Preload the customer's context on the FIRST keystroke of a message (empty -> typed), so the gather
  // runs while they finish composing and is cached by send-time (~1.4s off the turn's Context step). One
  // call per message, and an already-warm cache re-fetches nothing within its TTL — so this keeps the
  // downstream (customer-service) load to roughly one gather per turn rather than one per account opened.
  const handleDraftChange = (next) => {
    if (selectedCustomer?.id && !draft.text && next.text) warmContext(selectedCustomer.id);
    setDraft(next);
  };

  const remountEditor = () => setEditorKey((k) => k + 1);

  const handleSubmit = () => {
    const text = draft.text.trim();
    if (!selectedCustomer || !text) return;

    // Send the <colleague>-tagged form to the LLM, but show the raw "@name" text.
    const llmText = (draft.llmText || draft.text).trim();
    const mentions = draft.mentions;

    send({
      customerId: selectedCustomer.id,
      text: llmText,
      displayText: draft.text,
      mentions,
    });
    setAnalysedCustomer(selectedCustomer);

    // Clear the input for the next turn.
    setDraft({
      text: "",
      mentionedCustomerIds: [],
      mentionedUserIds: [],
      mentions: [],
      llmText: "",
    });
    remountEditor();
  };

  const handleSave = (content, mentions, conversationId, onSuccess, onSettled) => {
    const customerId = selectedCustomer?.id ?? null;
    saveMutation.mutate(
      {
        customerId,
        content,
        mentionedUserIds: mentions.map((m) => m.id),
        // Ties the note to the AI-chat thread so the intelligence-service records it there and the
        // Slack ping matches the turn.
        conversationId,
        // Optimistic feed row only (matches the canonical shape); the BFF resolves names itself.
        taggedUsers: mentions.map((m) => ({ userId: m.id, name: m.name })),
      },
      {
        // The Ontik read regenerates off the realtime ActivityCreated hint once the note
        // row is written (see CustomerContextPanel) — refreshing here would race that write.
        // useSaveSmartNote already fires the "Note added" toast, so don't duplicate it here.
        onSuccess,
        onSettled,
      },
    );
  };

  // Per-suggestion pending bookkeeping shared by accept and reject.
  const trackPending = (key) => {
    setPending((prev) => new Set([...prev, key]));
    return () =>
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
  };

  // Prepend an optimistic row to the customer's activity feed; the realtime
  // ActivityCreated hint reconciles the canonical feed (see CustomerContextPanel).
  const prependActivity = (customerId, row) => {
    if (!customerId) return;
    queryClient.setQueryData(["customerActivities", customerId], (old) => {
      if (!old?.pages?.length) return old;
      const [firstPage, ...rest] = old.pages;
      return {
        ...old,
        pages: [{ ...firstPage, items: [row, ...(firstPage?.items ?? [])] }, ...rest],
      };
    });
  };

  // Approve a suggested action through the BFF → intelligence-service, which relays to
  // the action service under this user's identity. Shared by the card's one-click accept
  // and the edit modal's accept-with-amendments; the card disables itself when actionId
  // is missing, so this always has one. `onApplied`/`onNotApplied` let the edit modal
  // close on success or surface the card message (e.g. refused amendments) and stay open.
  const acceptAction = (
    message,
    signalIndex,
    suggestion,
    { amendments, onApplied, onNotApplied } = {},
  ) => {
    const key = `${message.id}:${signalIndex}`;
    const markDone = () => setDone((prev) => new Set([...prev, key]));
    const onSettled = trackPending(key);

    decideAction.mutate(
      {
        actionId: suggestion.params?.actionId,
        conversationId: message.conversationId,
        customerId: analysedCustomer?.id,
        accept: true,
        amendments,
      },
      {
        onSuccess: (reply) => {
          if (reply.outcome !== "Applied") {
            // A one-click accept passes no onNotApplied — surface the server's message
            // (expired, already decided…) instead of silently doing nothing.
            if (onNotApplied) onNotApplied(reply);
            else
              toast({
                description:
                  reply.message ??
                  "The suggestion couldn't be applied — ask again in the chat.",
              });
            return;
          }
          onApplied?.();
          markDone();
          // The suggestion carries a model-authored account record; offer it as a note
          // draft now that the work is accepted (it was deliberately not a separate card
          // before acceptance). Registry-driven components (e.g. a task) opt out — their
          // acceptance is self-contained and shouldn't spawn a follow-up note.
          const note = suggestion.params?.note;
          if (
            !getSuggestionComponent(suggestion.type) &&
            typeof note === "string" &&
            note.trim()
          ) {
            appendSuggestion(message.id, {
              type: "CustomerInteractionNoted",
              label: "Add note",
              params: { summary: note.trim() },
            });
          }

          // useDecideAction itself fires no toast and patches no cache — replicate the
          // old direct-send flow's toast + optimistic activity-feed row here, or accepting
          // a payment request silently loses both. An amended accept shows the amount the
          // user actually approved, not the one the model proposed. A statement send is
          // semantically a document send, so it gets that wording despite travelling as a
          // payment request underneath.
          if (suggestion.type === "PaymentRequestRequested") {
            const step = editablePaymentStep(suggestion.params?.steps);
            const amendedPayload = step && amendments?.[step.stepId];
            const statement = isStatementStep(amendedPayload ?? step?.payload);
            const sentText = statement ? "Statement sent" : "Payment request sent";
            toast({ description: sentText });
            prependActivity(analysedCustomer?.id, {
              id: `optimistic-pr-${Date.now()}`,
              eventName: "PaymentRequestSent",
              title: sentText,
              amount: statement
                ? null
                : (amendedPayload?.amount_requested ??
                  paymentRequestAmount(suggestion.params?.steps)),
              invoicesRequested:
                (amendedPayload ?? step?.payload)?.invoices_requested ?? null,
              occurredAt: new Date().toISOString(),
            });
          }

          // Payment plans and promises to pay surface as "<kind> started". The
          // action→activities pipeline can lag ~30-60s (the action-service outbox
          // drains on a schedule), so show it immediately.
          if (
            (suggestion.type === "PromiseToPay" || suggestion.type === "PaymentPlan") &&
            suggestion.label
          ) {
            toast({ description: `${suggestion.label} started` });
            prependActivity(analysedCustomer?.id, {
              id: `optimistic-action-${suggestion.params?.actionId ?? Date.now()}`,
              eventName: "ActionStarted",
              title: `${suggestion.label} started`,
              body: suggestion.params?.explanation ?? null,
              // Carry the promise/schedule discriminator so the optimistic row reads with the
              // right identity (icon/colour) immediately — it matches the canonical row the feed
              // reconciles once the action→activities pipeline drains.
              scheduleKind: suggestion.params?.kind ?? null,
              occurredAt: new Date().toISOString(),
            });
          }

          if (isSendEmailType(suggestion.type)) {
            const attached =
              suggestion.params?.requestConfig?.documents?.length ?? 0;
            toast({ description: attached > 0 ? "Documents sent" : "Email sent" });
          }
        },
        onError: () =>
          toast({
            description: "Something went wrong and nothing was sent — please try again.",
          }),
        onSettled,
      },
    );
  };

  // Accept a registry-driven suggestion component (see suggestionComponents.js): the card's inline
  // edits become step amendments via the descriptor, and the shared acceptAction relays them. The
  // descriptor owns the after-accept side effects (e.g. refreshing Today's tasks) and success copy,
  // so the modal stays generic — a new component adds no branch here.
  const handleAcceptComponent = (message, signalIndex, suggestion, descriptor, values) => {
    if (!analysedCustomer) return;

    // A task recomposes its fan-out server-side (add/remove of a whole colleague can't be an amendment),
    // so it accepts via the dedicated task endpoint with the final assignee list, not step amendments.
    if (descriptor.acceptVia === "task") {
      const key = `${message.id}:${signalIndex}`;
      const onSettled = trackPending(key);
      const request = descriptor.buildAcceptRequest(values);
      acceptTask.mutate(
        {
          actionId: suggestion.params?.actionId,
          conversationId: message.conversationId,
          customerId: analysedCustomer?.id,
          ...request,
        },
        {
          onSuccess: (reply) => {
            if (reply.outcome !== "Applied") {
              toast({
                description:
                  reply.message ?? "The task couldn't be created — ask again in the chat.",
              });
              return;
            }
            descriptor.onAccepted?.({ queryClient, suggestion });
            if (descriptor.successMessage) toast({ description: descriptor.successMessage });
            setDone((prev) => new Set([...prev, key]));

            // The task→calendar→activities pipeline can lag ~30-60s (the action-service outbox
            // drains on a schedule), so show the row now; the realtime ActivityCreated hint
            // reconciles the canonical row. Matches the TaskCreated feed shape — title as the
            // subtitle, due date as the schedule, assignees resolved to names from their ids.
            prependActivity(analysedCustomer?.id, {
              id: `optimistic-task-${suggestion.params?.actionId ?? Date.now()}`,
              eventName: "TaskCreated",
              title: request.title || "Task",
              scheduledFor: request.dueAt ?? null,
              taggedUsers: (values.assignees ?? []).map((assignee) => ({
                userId: assignee.id,
                name: assignee.name,
              })),
              occurredAt: new Date().toISOString(),
            });

            // If the realtime hint is missed (the AppSync socket connects lazily with no replay),
            // this polls the feed until the canonical row lands and regenerates the Ontik read —
            // so it self-corrects in seconds rather than waiting for a refocus.
            contextPanelRef.current?.reconcileActivity();
          },
          onError: () =>
            toast({
              description: "Something went wrong and nothing was created — please try again.",
            }),
          onSettled,
        },
      );
      return;
    }

    acceptAction(message, signalIndex, suggestion, {
      amendments: descriptor.buildAmendments(suggestion, values),
      onApplied: () => {
        descriptor.onAccepted?.({ queryClient, suggestion });
        if (descriptor.successMessage) toast({ description: descriptor.successMessage });
      },
      // Refused amendments (or a stale card) surface the message and leave the card in place.
      onNotApplied: (reply) => toast({ description: reply.message }),
    });
  };

  // Edit-then-accept: open the batch request modal seeded from the suggestion's
  // payment.request.send step; its Accept turns the edited values into step amendments.
  const handleEditSuggestion = (message, signalIndex, suggestion) => {
    if (!analysedCustomer) return;
    if (isSendEmailType(suggestion.type)) {
      setEditDocsTarget({ message, signalIndex, suggestion });
      return;
    }
    setEditTarget({ message, signalIndex, suggestion });
  };

  const closeEdit = () => setEditTarget(null);
  const closeDocsEdit = () => setEditDocsTarget(null);

  // Opening a payment request from the activity feed's plan modal. The plan modal delegates the
  // open up to here rather than stacking its own drawer inside this modal; we render the drawer at
  // the top level (a sibling of the dialog, so it outlives it) and close the whole smart-note flow,
  // leaving just the drawer. Closing the drawer returns the user to the page, not back into here.
  const [paymentRequestId, setPaymentRequestId] = useState(null);
  // The drawer's "View details" round-trips back to the plan; we reopen it at the top level too
  // (the in-flow one is gone with the closed dialog), and it delegates PR opens back to the drawer.
  const [planActionId, setPlanActionId] = useState(null);
  const handleOpenPaymentRequest = useCallback(
    (id) => {
      if (!id) return;
      setPaymentRequestId(id);
      onClose();
    },
    [onClose],
  );
  // From the top-level plan modal, swap it out for the drawer (rather than stacking).
  const openPaymentRequestFromPlan = useCallback((id) => {
    if (!id) return;
    setPlanActionId(null);
    setPaymentRequestId(id);
  }, []);

  // Edit-then-accept for a document email: the preview modal hands back the edited subject/body and
  // the kept attachments, which become email.send step amendments on the accept.
  const handleAcceptDocsEdited = ({ subject, title, body, attachmentLocations, recipients, cc, bcc }) => {
    if (!editDocsTarget) return;
    const { message, signalIndex, suggestion } = editDocsTarget;
    acceptAction(message, signalIndex, suggestion, {
      amendments: buildEmailAmendments({
        steps: suggestion.params?.steps,
        subject,
        title,
        body,
        attachmentLocations,
        recipients,
        cc,
        bcc,
      }),
      onApplied: closeDocsEdit,
      onNotApplied: (reply) => toast({ description: reply.message }),
    });
  };

  const handleAcceptEdited = ({ values, strategy, account, cc, bcc }) => {
    if (!editTarget) return;
    const { message, signalIndex, suggestion } = editTarget;
    acceptAction(message, signalIndex, suggestion, {
      amendments: buildAmendments({
        steps: suggestion.params?.steps,
        values,
        strategy,
        account,
        cc,
        bcc,
      }),
      onApplied: closeEdit,
      // Refused amendments (or a stale card) surface the card message and keep the
      // modal open so the user can correct the values and try again.
      onNotApplied: (reply) => toast({ description: reply.message }),
    });
  };

  const editStep = editTarget
    ? editablePaymentStep(editTarget.suggestion.params?.steps)
    : null;

  // Accept a suggestion from a given assistant turn; the clicked card shows a
  // loader while in flight and flips to a tick on success (it stays on screen).
  const handleAcceptSuggestion = (message, signalIndex, suggestion, content, mentions) => {
    const key = `${message.id}:${signalIndex}`;
    const markDone = () => setDone((prev) => new Set([...prev, key]));

    // A cancellation confirms through the calendar-style modal, not the card itself: opening it
    // is all Confirm does here — performCancellation (below) is what actually cancels.
    if (suggestion.type === "CancellationRequested") {
      setCancelTarget({ message, signalIndex, suggestion });
      return;
    }

    // Send-documents is action-backed now (an email.send step): a bare Send approves the action as
    // composed (model subject/body + all materialized attachments); Edit opens the preview modal.
    if (isActionBacked(suggestion.type)) {
      acceptAction(message, signalIndex, suggestion);
      return;
    }

    if (suggestion.type === "CustomerInteractionNoted") {
      handleSave(
        content ?? suggestion.params?.summary ?? "",
        mentions ?? message.mentions ?? [],
        message.conversationId,
        markDone,
        trackPending(key),
      );
      return;
    }

    // Unknown suggestion type: do nothing rather than silently creating an empty note
    // (which is what the old fallthrough did).
  };

  // Run the cancellation once the user confirms in the modal — the card's Confirm only opens the
  // gate. The target rode the signal as extracted.targetActionId; every resolved outcome carries
  // a user-presentable message, and only Applied flips the tick.
  const performCancellation = () => {
    if (!cancelTarget) return;
    const { message, signalIndex, suggestion } = cancelTarget;
    const key = `${message.id}:${signalIndex}`;
    const markDone = () => setDone((prev) => new Set([...prev, key]));
    const onSettled = trackPending(key);
    cancelAction.mutate(
      {
        actionId: suggestion.params?.targetActionId,
        conversationId: message.conversationId,
        customerId: analysedCustomer?.id,
        scope: suggestion.params?.scope,
      },
      {
        onSuccess: (reply) => {
          toast({ description: reply.message });
          if (reply.outcome !== "Applied") return;
          markDone();
          // The action→activities pipeline lags (outbox drain), so show the row now —
          // titled and detailed from the confirmed card's params, matching the real row.
          const copy = cancelledActivityCopy(suggestion.params);
          prependActivity(analysedCustomer?.id, {
            id: `optimistic-cancel-${suggestion.params?.targetActionId ?? Date.now()}`,
            eventName: "ActionCancelled",
            title: copy.title,
            body: copy.body,
            occurredAt: new Date().toISOString(),
          });
        },
        onError: () =>
          toast({
            description:
              "Something went wrong and nothing was cancelled — please try again.",
          }),
        onSettled: () => {
          onSettled();
          setCancelTarget(null);
        },
      },
    );
  };

  // Reject a suggestion — the card flips to a greyed-out cross everywhere. A suggestion backed by a
  // pre-created action (actionId, not yet expired) records the rejection with the action service and
  // on the conversation thread; everything else (notes, cancellations) has no server action to
  // reject, so it's flipped client-side only.
  const handleRejectSuggestion = (message, signalIndex, suggestion) => {
    const key = `${message.id}:${signalIndex}`;
    const markRejected = () => setRejected((prev) => new Set([...prev, key]));

    const actionId = suggestion.params?.actionId;
    const notExpired =
      !suggestion.params?.expiresAt ||
      new Date(suggestion.params.expiresAt) > new Date();
    if (!actionId || !notExpired) {
      markRejected();
      return;
    }

    const onSettled = trackPending(key);
    decideAction.mutate(
      {
        actionId,
        conversationId: message.conversationId,
        customerId: analysedCustomer?.id,
        accept: false,
      },
      {
        onSuccess: markRejected,
        onError: () =>
          toast({
            description:
              "Something went wrong and the rejection wasn't recorded — please try again.",
          }),
        onSettled,
      },
    );
  };

  // Clear everything when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      clearConversation();
      setSelectedCustomer(null);
      setDraft({
        text: "",
        mentionedCustomerIds: [],
        mentionedUserIds: [],
        mentions: [],
        llmText: "",
      });
      remountEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-zinc-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel
              transition
              className="pointer-events-none flex items-start gap-4 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="pointer-events-auto flex w-[640px] flex-col gap-4">
                <div className="flex flex-col rounded-2xl bg-zinc-50 text-left shadow-xl">
                  <div className="flex items-center justify-between p-5 pb-0">
                    <h2 className="text-xl font-semibold text-zinc-900">
                      {title}
                    </h2>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setHelpOpen(true)}
                        title="See what Ontik can do"
                        className="flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white py-2 pl-1.5 pr-2.5 text-xs font-semibold text-zinc-600 shadow-sm transition-colors hover:border-zinc-300 hover:text-zinc-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                      >
                        <AiSparkleIcon className="size-4" />
                        Discover
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md text-zinc-400 hover:text-zinc-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon aria-hidden="true" className="size-6" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 p-5 pt-3">
                    {/* Conversation thread (scrolls; input stays pinned below) */}
                    {(messages.length > 0 || isThinking) && (
                      <div
                        ref={scrollRef}
                        className="flex max-h-[50vh] flex-col gap-4 overflow-y-auto"
                      >
                        {messages.map((message) =>
                          message.role === "user" ? (
                            <div key={message.id} className="flex flex-col items-end gap-1">
                              <div className="max-w-[85%] rounded-2xl bg-white px-4 py-2.5 text-sm text-zinc-900 shadow-sm">
                                <PromptText
                                  text={message.text}
                                  mentionNames={(message.mentions ?? []).map((m) => m.name)}
                                />
                              </div>
                            </div>
                          ) : (
                            <div key={message.id} className="flex flex-col gap-3">
                              {message.reply && (
                                <AssistantReply
                                  text={message.reply}
                                  onGrow={message.signals?.length ? undefined : scrollToBottom}
                                />
                              )}
                              {/* Reply and cards render in the same paint and share the same fade, so they
                                  land together — no gate between them. Scroll is owned by the card when
                                  there is one (timeline cards position themselves; others pin the thread),
                                  so the reply only scrolls on a pure-chat turn with nothing below it. */}
                              {chartsEnabled &&
                                (message.visualizations ?? []).map((chart) => (
                                  <div key={chart.id} className="animate-fadeIn">
                                    <ChartCard spec={chart} />
                                  </div>
                                ))}
                              {message.signals.map((s, i) => {
                                // A registry-driven component renders the generic editable card;
                                // every other type falls back to the legacy DraftSuggestionCard. Each card
                                // shares the reply's fade so they materialize together.
                                const descriptor = getSuggestionComponent(s.type);
                                return descriptor ? (
                                  <SuggestionComponentCard
                                    key={`${message.id}:${i}`}
                                    suggestion={s}
                                    descriptor={descriptor}
                                    index={i}
                                    isSending={pending.has(`${message.id}:${i}`)}
                                    isDone={done.has(`${message.id}:${i}`)}
                                    isRejected={rejected.has(`${message.id}:${i}`)}
                                    onAccept={(values) =>
                                      handleAcceptComponent(message, i, s, descriptor, values)
                                    }
                                    onReject={() => handleRejectSuggestion(message, i, s)}
                                    onGrow={scrollToBottom}
                                  />
                                ) : (
                                  <DraftSuggestionCard
                                    key={`${message.id}:${i}`}
                                    suggestion={s}
                                    index={i}
                                    isSending={pending.has(`${message.id}:${i}`)}
                                    isDone={done.has(`${message.id}:${i}`)}
                                    isRejected={rejected.has(`${message.id}:${i}`)}
                                    onAccept={(content, mentions) =>
                                      handleAcceptSuggestion(message, i, s, content, mentions)
                                    }
                                    onReject={() => handleRejectSuggestion(message, i, s)}
                                    onEdit={() => handleEditSuggestion(message, i, s)}
                                    taggedUsers={message.mentions ?? []}
                                    customer={analysedCustomer}
                                    onGrow={scrollToBottom}
                                    scrollContainerRef={scrollRef}
                                    onPreviewStep={(step, instalment) =>
                                      setPreview({ step, instalment })
                                    }
                                  />
                                );
                              })}
                            </div>
                          ),
                        )}

                        {/* Loading — only until the reply starts streaming. Once the reply is flowing,
                            the streaming text itself is the "working" signal, so the separate spinner is
                            dropped rather than left sitting beneath a live reply. */}
                        {isThinking &&
                          !(
                            messages[messages.length - 1]?.role === "assistant" &&
                            (messages[messages.length - 1]?.reply ||
                              messages[messages.length - 1]?.signals?.length)
                          ) && (
                            <div
                              className="flex items-center gap-2 text-sm text-zinc-400"
                              aria-live="polite"
                            >
                              <AiSpinner size={16} />
                              <span
                                key={thinkingLabel ?? THINKING_PLACEHOLDER}
                                className="animate-fadeIn"
                              >
                                {thinkingLabel ?? THINKING_PLACEHOLDER}
                              </span>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Response-time readout: Enter → suggestion card shown (not clickable). Gated
                        behind the aiResponseTimer PostHog flag so most users don't see it. */}
                    {responseTimerEnabled && lastCardMs != null && (
                      <div className="self-start rounded-full bg-zinc-900 px-2.5 py-1 font-mono text-xs text-white">
                        ⏱ suggestion shown in {(lastCardMs / 1000).toFixed(2)}s
                      </div>
                    )}

                    {/* Input row */}
                    <div className="rounded-2xl border border-zinc-300 bg-white p-3 focus-within:outline focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-primary">
                      <MentionTextarea
                        key={editorKey}
                        bare
                        placeholder={PROMPT_PLACEHOLDER}
                        onChange={handleDraftChange}
                        onCustomerSelect={setSelectedCustomer}
                        selectedCustomer={selectedCustomer}
                        onSubmit={handleSubmit}
                        readLoading={Boolean(selectedCustomer) && !readReady}
                        loadingPlaceholder={READ_LOADING_PLACEHOLDER}
                      />

                      <div className="mt-3 flex items-center justify-between gap-2">
                        {selectedCustomer ? (
                          <AccountChip
                            customer={selectedCustomer}
                            onClear={() => setSelectedCustomer(null)}
                          />
                        ) : (
                          <span />
                        )}

                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={!canSubmit}
                          aria-label="Send"
                          className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-white transition-colors hover:bg-primary-600 active:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:bg-primary-300"
                        >
                          <ArrowUpIcon aria-hidden="true" className="size-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer context panel — only once an account is set */}
              {selectedCustomer && (
                <div className="pointer-events-auto">
                  <CustomerContextPanel
                    ref={contextPanelRef}
                    customer={selectedCustomer}
                    onClose={() => setSelectedCustomer(null)}
                    onViewTradeAccount={onClose}
                    onReadyChange={setReadReady}
                    onPreviewStep={(step, instalment) => setPreview({ step, instalment })}
                    onOpenPaymentRequest={handleOpenPaymentRequest}
                  />
                </div>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <BatchTemplateRequestModal
        isOpen={!!editTarget}
        onClose={closeEdit}
        customerData={editTarget && requestCustomer ? [requestCustomer] : []}
        requestConfig={editStep ? stepToRequestConfig(editStep.payload) : null}
        skipTemplateSelect
        onAcceptAction={handleAcceptEdited}
        acceptPending={decideAction.isPending}
      />

      <BatchTemplateRequestModal
        readOnly
        isOpen={!!preview}
        onClose={() => setPreview(null)}
        customerData={preview && requestCustomer ? [requestCustomer] : []}
        requestConfig={preview ? stepToRequestConfig(preview.step.payload) : null}
        readOnlyTitle={
          preview?.instalment && preview.instalment.total > 1
            ? `Instalment ${preview.instalment.number} of ${preview.instalment.total}`
            : undefined
        }
        skipTemplateSelect
      />

      <SendEmailPreviewModal
        isOpen={!!editDocsTarget}
        suggestion={editDocsTarget?.suggestion}
        customer={analysedCustomer}
        onClose={closeDocsEdit}
        onSend={handleAcceptDocsEdited}
        sending={decideAction.isPending}
      />

      <CancelActionConfirmModal
        isOpen={!!cancelTarget}
        params={cancelTarget?.suggestion?.params}
        customerName={analysedCustomer?.name}
        onConfirm={performCancellation}
        onClose={() => setCancelTarget(null)}
        submitting={cancelAction.isPending}
      />

      {/* Payment request drawer opened from the activity feed's plan modal. Lives here (outside the
          dialog) so it outlives the closing smart-note flow; rendered elevated to sit above everything.
          "View details" reopens the plan at the top level, which delegates PR opens back to the drawer. */}
      <PaymentRequestDetails
        paymentRequestId={paymentRequestId}
        elevated
        onChange={() => {}}
        onViewPlan={(actionId) => {
          setPaymentRequestId(null);
          setPlanActionId(actionId);
        }}
        onClose={() => setPaymentRequestId(null)}
        compactActions
      />

      <PlanDetailModal
        actionId={planActionId}
        isOpen={!!planActionId}
        elevated
        onClose={() => setPlanActionId(null)}
        onOpenPaymentRequest={openPaymentRequestFromPlan}
      />

      <CapabilitiesHelp isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}
